/**
 * Guest details collection and mapping for Mews Booking Engine API.
 * Step 4.1: Validate guest info, map to Mews Customer/Booker, respect RequiredFields from Configuration.
 *
 * @see MEWS_API_IMPLEMENTATION_PLAN.md - Step 4.1
 * @see reservation-groups.md - Customer, Send marketing emails
 * @see configuration.md - Required field (e.g. Telephone)
 */

import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import type { MewsCustomer, RequiredField } from './types';

/** Optional overrides for validateGuestDetails (e.g. /book page i18n). */
export interface GuestDetailsValidationMessages {
  firstNameRequired?: string;
  lastNameRequired?: string;
  emailRequired?: string;
  emailInvalid?: string;
  telephoneRequired?: string;
  telephoneTooShort?: string;
  telephoneTooLong?: string;
  telephoneInvalidLength?: string;
  telephoneLengthInvalid?: string;
  telephoneInvalid?: string;
  bookerFirstNameRequired?: string;
  bookerLastNameRequired?: string;
  bookerEmailRequired?: string;
  bookerEmailInvalid?: string;
  bookerTelephoneRequired?: string;
}

function telephoneLengthMessage(
  result: 'TOO_SHORT' | 'TOO_LONG' | 'INVALID_LENGTH',
  m?: GuestDetailsValidationMessages
): string {
  switch (result) {
    case 'TOO_SHORT':
      return m?.telephoneTooShort ?? 'Phone number is too short for the selected country.';
    case 'TOO_LONG':
      return m?.telephoneTooLong ?? 'Phone number is too long for the selected country.';
    case 'INVALID_LENGTH':
      return m?.telephoneInvalidLength ?? 'Phone number has an invalid length for the selected country.';
    default:
      return m?.telephoneInvalid ?? 'Please enter a valid phone number.';
  }
}

/** Local form state for guest (customer) and optional booker. */
export interface GuestDetailsForm {
  firstName: string;
  lastName: string;
  email: string;
  telephone: string;
  nationalityCode: string;
  /** Guest/customer marketing opt-in. When booking on behalf, only booker's choice is sent for marketing. */
  sendMarketingEmails: boolean | null;
  /** Special requests / preferences (stored in reservation Notes). */
  notes: string;
  /** If true, booker fields are used and Booker object is sent; Customer.SendMarketingEmails set to false/null. */
  isBookingOnBehalf: boolean;
  bookerFirstName: string;
  bookerLastName: string;
  bookerEmail: string;
  bookerTelephone: string;
  bookerSendMarketingEmails: boolean | null;
}

export const defaultGuestDetailsForm: GuestDetailsForm = {
  firstName: '',
  lastName: '',
  email: '',
  telephone: '',
  nationalityCode: '',
  sendMarketingEmails: null,
  notes: '',
  isBookingOnBehalf: false,
  bookerFirstName: '',
  bookerLastName: '',
  bookerEmail: '',
  bookerTelephone: '',
  bookerSendMarketingEmails: null,
};

/** Validation result: keys are field names, values are error messages. */
export interface GuestDetailsValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  telephone?: string;
  bookerFirstName?: string;
  bookerLastName?: string;
  bookerEmail?: string;
  bookerTelephone?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** ISO 3166-1 Alpha-2: exactly 2 uppercase letters (optional field). */
const NATIONALITY_REGEX = /^[A-Z]{2}$/;

/**
 * Validates guest details: format and required fields from Configuration.
 * Required: Email, FirstName, LastName. Telephone if in requiredFields.
 * When isBookingOnBehalf: booker Email, FirstName, LastName required; booker Telephone if in requiredFields.
 */
export function validateGuestDetails(
  form: GuestDetailsForm,
  requiredFields: RequiredField[] = [],
  messages?: GuestDetailsValidationMessages
): GuestDetailsValidationErrors {
  const errors: GuestDetailsValidationErrors = {};
  const msg = messages;

  const requireTelephone = requiredFields.includes('Telephone');

  // Customer (guest) - required always
  if (!form.firstName?.trim()) {
    errors.firstName = msg?.firstNameRequired ?? 'First name is required.';
  }
  if (!form.lastName?.trim()) {
    errors.lastName = msg?.lastNameRequired ?? 'Last name is required.';
  }
  if (!form.email?.trim()) {
    errors.email = msg?.emailRequired ?? 'Email is required.';
  } else if (!EMAIL_REGEX.test(form.email.trim())) {
    errors.email = msg?.emailInvalid ?? 'Please enter a valid email address.';
  }
  if (requireTelephone && !form.telephone?.trim()) {
    errors.telephone = msg?.telephoneRequired ?? 'Phone number is required.';
  } else if (form.telephone?.trim()) {
    const trimmed = form.telephone.trim();
    // isPossiblePhoneNumber: fast length check for the region (country-specific).
    if (!isPossiblePhoneNumber(trimmed)) {
      const lengthResult = validatePhoneNumberLength(trimmed);
      errors.telephone =
        lengthResult === 'TOO_SHORT' || lengthResult === 'TOO_LONG' || lengthResult === 'INVALID_LENGTH'
          ? telephoneLengthMessage(lengthResult, msg)
          : msg?.telephoneLengthInvalid ?? 'Phone number length is invalid for the selected country.';
    } else if (!isValidPhoneNumber(trimmed)) {
      // isValidPhoneNumber: stricter check (prefixes and digit patterns for the country).
      errors.telephone = msg?.telephoneInvalid ?? 'Please enter a valid phone number.';
    }
  }

  if (form.nationalityCode?.trim() && !NATIONALITY_REGEX.test(form.nationalityCode.trim())) {
    // Optional field; if provided, must be valid ISO 3166-1 Alpha-2
    // We don't have a dedicated error key for nationality in the plan; could add or ignore. Keeping validation strict.
    // Use a generic approach: we normalize to uppercase in mapper; invalid code can be omitted in mapper.
  }

  if (form.isBookingOnBehalf) {
    if (!form.bookerFirstName?.trim()) {
      errors.bookerFirstName = msg?.bookerFirstNameRequired ?? 'Booker first name is required.';
    }
    if (!form.bookerLastName?.trim()) {
      errors.bookerLastName = msg?.bookerLastNameRequired ?? 'Booker last name is required.';
    }
    if (!form.bookerEmail?.trim()) {
      errors.bookerEmail = msg?.bookerEmailRequired ?? 'Booker email is required.';
    } else if (!EMAIL_REGEX.test(form.bookerEmail.trim())) {
      errors.bookerEmail = msg?.bookerEmailInvalid ?? 'Please enter a valid booker email address.';
    }
    if (requireTelephone && !form.bookerTelephone?.trim()) {
      errors.bookerTelephone = msg?.bookerTelephoneRequired ?? 'Booker phone number is required.';
    } else if (form.bookerTelephone?.trim()) {
      const trimmed = form.bookerTelephone.trim();
      if (!isPossiblePhoneNumber(trimmed)) {
        const lengthResult = validatePhoneNumberLength(trimmed);
        errors.bookerTelephone =
          lengthResult === 'TOO_SHORT' || lengthResult === 'TOO_LONG' || lengthResult === 'INVALID_LENGTH'
            ? telephoneLengthMessage(lengthResult, msg)
            : msg?.telephoneLengthInvalid ?? 'Phone number length is invalid for the selected country.';
      } else if (!isValidPhoneNumber(trimmed)) {
        errors.bookerTelephone = msg?.telephoneInvalid ?? 'Please enter a valid phone number.';
      }
    }
  }

  return errors;
}

/**
 * Maps local guest details to Mews Customer (and optionally Booker).
 * - When Booker is present: Customer.SendMarketingEmails is set to false (booker is subscribing, not customer).
 * - Booker.SendMarketingEmails is set from booker's choice.
 * - NationalityCode: only included if valid ISO 3166-1 Alpha-2 (e.g. "CH", "US").
 * - Empty strings for optional fields are sent as "" per Mews example, or can be omitted; Mews docs show Telephone: "".
 */
export function mapGuestDetailsToMewsCustomer(form: GuestDetailsForm): {
  customer: MewsCustomer;
  booker?: MewsCustomer;
} {
  const nationality =
    form.nationalityCode?.trim().length === 2
      ? form.nationalityCode.trim().toUpperCase()
      : undefined;

  const customer: MewsCustomer = {
    Email: form.email.trim(),
    FirstName: form.firstName.trim(),
    LastName: form.lastName.trim(),
    Telephone: form.telephone?.trim() || undefined,
    NationalityCode: nationality,
    SendMarketingEmails: form.isBookingOnBehalf ? false : form.sendMarketingEmails ?? undefined,
  };

  if (form.isBookingOnBehalf && form.bookerEmail?.trim() && form.bookerFirstName?.trim() && form.bookerLastName?.trim()) {
    const booker: MewsCustomer = {
      Email: form.bookerEmail.trim(),
      FirstName: form.bookerFirstName.trim(),
      LastName: form.bookerLastName.trim(),
      Telephone: form.bookerTelephone?.trim() || undefined,
      SendMarketingEmails: form.bookerSendMarketingEmails ?? undefined,
    };
    return { customer, booker };
  }

  return { customer };
}
